import React, { useEffect, useMemo, useState } from "react";
import {
  fetchPublicHolidays,
  createPublicHoliday,
  updatePublicHoliday,
  deletePublicHolidayAPI,
} from "./api";
import { toast } from "react-toastify";

const REGIONS_GB = [
  { code: "EW", label: "England & Wales" },
  { code: "SC", label: "Scotland" },
  { code: "NI", label: "Northern Ireland" },
];

function PublicHolidays() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [country, setCountry] = useState("GB");
  const [region, setRegion] = useState("EW");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // draft pour ajout
  const [newRow, setNewRow] = useState({
    holiday_date: "",
    name: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await fetchPublicHolidays(year, country, region);
      setRows(data);
    } catch {
      toast.error("Failed to fetch public holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, country, region]);

  const existingKeySet = useMemo(() => {
    return new Set(
      rows.map((r) => `${r.holiday_date}|${r.country_code}|${r.region_code}`)
    );
  }, [rows]);

  const onCreate = async () => {
    if (!newRow.holiday_date) {
      toast.error("Please pick a date");
      return;
    }
    const key = `${newRow.holiday_date}|${country}|${region}`;
    if (existingKeySet.has(key)) {
      toast.error("This holiday already exists for the selected region.");
      return;
    }
    try {
      await createPublicHoliday({
        holiday_date: newRow.holiday_date,
        country_code: country,
        region_code: region,
        name: newRow.name || null,
      });
      toast.success("Public holiday added");
      setNewRow({ holiday_date: "", name: "" });
      load();
    } catch {
      toast.error("Failed to add public holiday");
    }
  };

  const onUpdate = async (row) => {
    if (!row.holiday_date) return;
    try {
      await updatePublicHoliday(row.id, {
        holiday_date: row.holiday_date,
        country_code: row.country_code,
        region_code: row.region_code,
        name: row.name || null,
      });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this public holiday?")) return;
    try {
      await deletePublicHolidayAPI(id);
      toast.success("Deleted");
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const departmentChip = (code) => {
    const m = REGIONS_GB.find((r) => r.code === code);
    return m?.label || code;
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6 text-center">
        Public Holidays
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Year
          </label>
          <input
            type="number"
            value={year}
            onChange={(e) =>
              setYear(Number(e.target.value || new Date().getFullYear()))
            }
            className="w-full px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          >
            <option value="GB">United Kingdom (GB)</option>
            {/* Tu peux en ajouter ici si tu élargis */}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Region
          </label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          >
            {REGIONS_GB.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          {loading ? (
            <span className="text-gray-500">Loading…</span>
          ) : (
            <button
              onClick={load}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-4 py-2 rounded"
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 p-4 bg-yellow-50 border rounded text-sm text-gray-700">
        These holidays are used by the system to <strong>exclude</strong> days
        from holiday-hour calculations (Mon–Thu only, per your rules). Make sure
        they are correct for the chosen region.
      </div>

      {/* Add new holiday */}
      <div className="mb-4 border rounded p-4">
        <h2 className="font-semibold mb-3">Add a Public Holiday</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={newRow.holiday_date}
              onChange={(e) =>
                setNewRow({ ...newRow, holiday_date: e.target.value })
              }
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-700 mb-1">
              Name (optional)
            </label>
            <input
              type="text"
              placeholder="e.g., Early May Bank Holiday"
              value={newRow.name}
              onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        </div>
        <div className="mt-3 text-right">
          <button
            onClick={onCreate}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Add Holiday
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded shadow-sm">
          <thead className="bg-gold">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white uppercase">
                Date
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white uppercase">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white uppercase">
                Country
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white uppercase">
                Region
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-white uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r, idx) => (
              <tr
                key={r.id || `${r.holiday_date}-${idx}`}
                className="hover:bg-gray-50"
              >
                <td className="px-3 py-2">
                  <input
                    type="date"
                    value={r.holiday_date}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((x) =>
                          x.id === r.id
                            ? { ...x, holiday_date: e.target.value }
                            : x
                        )
                      )
                    }
                    className="border rounded px-2 py-1"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={r.name || ""}
                    placeholder="(optional)"
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((x) =>
                          x.id === r.id ? { ...x, name: e.target.value } : x
                        )
                      )
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={r.country_code}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((x) =>
                          x.id === r.id
                            ? {
                                ...x,
                                country_code: e.target.value.toUpperCase(),
                              }
                            : x
                        )
                      )
                    }
                    className="border rounded px-2 py-1 w-20"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={r.region_code}
                    onChange={(e) =>
                      setRows((prev) =>
                        prev.map((x) =>
                          x.id === r.id
                            ? { ...x, region_code: e.target.value }
                            : x
                        )
                      )
                    }
                    className="border rounded px-2 py-1"
                  >
                    {REGIONS_GB.map((rr) => (
                      <option key={rr.code} value={rr.code}>
                        {rr.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 flex gap-2">
                  <button
                    onClick={() => onUpdate(r)}
                    className="bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => onDelete(r.id)}
                    className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-1 rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-500" colSpan={5}>
                  No public holidays found for {year} ({departmentChip(region)}
                  ).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PublicHolidays;
